import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Button } from '@/components/ui/button';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// Make sure to call loadStripe outside of a component's render to avoid
// recreating the Stripe object on every render
let stripePromise: Promise<Stripe | null> | null = null;
try {
  const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY as string;
  if (stripeKey) {
    console.log('Initializing Stripe with public key');
    stripePromise = loadStripe(stripeKey);
  } else {
    console.warn('Missing Stripe public key (VITE_STRIPE_PUBLIC_KEY)');
    stripePromise = null;
  }
} catch (error) {
  console.error('Error initializing Stripe:', error);
  stripePromise = null;
}

type CheckoutFormProps = {
  clientSecret: string;
  planName: string;
  onSuccess: () => void;
  onCancel: () => void;
};

function CheckoutForm({ clientSecret, planName, onSuccess, onCancel }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/payment-success?planName=' + encodeURIComponent(planName),
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'An unexpected error occurred.');
      toast({
        title: 'Payment failed',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment completed successfully
      console.log('Payment succeeded with payment intent:', paymentIntent.id);
      
      // Clear pending payment state immediately after successful payment
      sessionStorage.removeItem('pendingPayment');
      sessionStorage.removeItem('pendingCheckout');
      
      toast({
        title: 'Subscription activated',
        description: `You are now subscribed to the ${planName} plan!`,
      });
      
      // Force refresh user data to prevent subscription page from showing
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/subscription-status"] });
      
      // Redirect to payment success page
      window.location.href = `/payment-success?planName=${encodeURIComponent(planName)}`;
    } else if (paymentIntent) {
      // Payment requires additional steps (like 3D Secure)
      console.log('Payment requires additional actions:', paymentIntent.status);
      toast({
        title: 'Additional verification required',
        description: 'Please complete the additional verification steps.',
      });
      // The redirect will happen automatically via the return_url
    } else {
      // Handle the case where we don't get an error or payment intent
      toast({
        title: 'Subscription processing',
        description: `Your subscription is being processed. You'll be redirected to your dashboard shortly.`,
      });
      // Fallback success handler
      setTimeout(() => onSuccess(), 2000);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Payment failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      
      <PaymentElement />
      
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || isProcessing}>
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Subscribe to ${planName}`
          )}
        </Button>
      </div>
    </form>
  );
}

type StripeCheckoutProps = {
  planId: string | number;
  planName: string;
  priceId?: string;
  onSuccess: () => void;
  onCancel: () => void;
};

export default function StripeCheckout({ planId, planName, priceId, onSuccess, onCancel }: StripeCheckoutProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const createSubscription = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log(`Creating subscription with planId: ${planId}, priceId: ${priceId || 'not provided'}`);

        const response = await apiRequest('/api/subscriptions/create', {
          method: 'POST',
          data: { 
            planId: planId.toString(), // Ensure planId is sent as string for consistency
            priceId // Pass the specific price ID if available
          },
        });
        
        const data = await response.json();
        
        if (!data.clientSecret) {
          throw new Error('No client secret returned from the server.');
        }
        
        console.log('Subscription created successfully, client secret received');
        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error('Error creating subscription:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to create subscription';
        setError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    createSubscription();
  }, [planId, priceId, toast]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Setting up your subscription...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={() => window.location.reload()} className="w-full sm:w-1/2">
            Try Again
          </Button>
          <Button variant="secondary" onClick={onCancel} className="w-full sm:w-1/2">
            Change Plan
          </Button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Unable to initialize payment process. Please try again later.</AlertDescription>
        </Alert>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={() => window.location.reload()} className="w-full sm:w-1/2">
            Try Again
          </Button>
          <Button variant="secondary" onClick={onCancel} className="w-full sm:w-1/2">
            Change Plan
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Complete your subscription</h3>
        <p className="text-sm text-muted-foreground">
          You're subscribing to the <strong>{planName}</strong> plan. Enter your payment details below.
        </p>
        <CheckoutForm 
          clientSecret={clientSecret} 
          planName={planName} 
          onSuccess={onSuccess} 
          onCancel={onCancel} 
        />
      </div>
    </Elements>
  );
}